import React, { useEffect, useRef, useState } from 'react';
import { cn } from 'design-system/react';

interface HoldToClickButtonProps {
  onComplete: () => void;
  holdTime?: number;
  colorScheme?: 'red' | 'green' | 'blue' | 'yellow' | 'orange';
  isLoading?: boolean;
  children: React.ReactNode;
}

const colorClasses: Record<string, string> = {
  red: 'bg-action-danger hover:bg-action-danger-hover text-text-inverse',
  green: 'bg-feedback-success hover:brightness-90 text-text-inverse',
  blue: 'bg-action-primary hover:bg-action-primary-hover text-text-inverse',
  yellow: 'bg-state-paused hover:brightness-90 text-text-primary',
  orange: 'bg-state-running hover:brightness-90 text-text-inverse',
};

export const HoldToClickButton: React.FC<HoldToClickButtonProps> = ({
  onComplete,
  holdTime = 2000,
  children,
  colorScheme = 'red',
  isLoading,
}) => {
  const [isHolding, setIsHolding] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<number | null>(null);
  const startTimeRef = useRef(0);

  useEffect(() => {
    return () => {
      if (timerRef.current !== null) {
        cancelAnimationFrame(timerRef.current);
      }
    };
  }, []);

  const startHolding = () => {
    setIsHolding(true);
    startTimeRef.current = Date.now();
    animateHold();
  };

  const stopHolding = () => {
    setIsHolding(false);
    setProgress(0);
    if (timerRef.current !== null) {
      cancelAnimationFrame(timerRef.current);
      timerRef.current = null;
    }
  };

  const animateHold = () => {
    const elapsedTime = Date.now() - startTimeRef.current;
    const newProgress = Math.min((elapsedTime / holdTime) * 100, 100);
    setProgress(newProgress);

    if (newProgress < 100) {
      timerRef.current = requestAnimationFrame(animateHold);
    } else {
      onComplete();
      stopHolding();
    }
  };

  return (
    <button
      className={cn(
        'relative px-4 py-2 rounded-md font-medium text-sm transition-colors select-none',
        colorClasses[colorScheme] ?? colorClasses.red,
        isLoading && 'opacity-60 cursor-not-allowed',
      )}
      disabled={isLoading}
      onMouseDown={startHolding}
      onMouseUp={stopHolding}
      onMouseLeave={stopHolding}
      onTouchStart={startHolding}
      onTouchEnd={stopHolding}
    >
      {isLoading ? '...' : children}
      {isHolding && (
        <div
          className="absolute bottom-0 left-0 h-1 bg-feedback-success transition-[width] duration-100 ease-linear rounded-b-md"
          style={{ width: `${progress}%` }}
        />
      )}
    </button>
  );
};
